<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
    <div class="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-md">
      <div
        class="relative flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">
          Mobile POS
        </h1>
        <div class="absolute left-1/2 -translate-x-1/2 -top-2">
          <button
            @click="toggleSettingsPopover"
            class="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all border-4 border-white dark:border-gray-800"
            :class="{ 'rotate-180': showSettings }"
          >
            <svg
              class="w-5 h-5 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
        <button
          @click="$router.back()"
          class="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <POSSettingsPopover
        :is-visible="showSettings"
        v-if="showSettings"
        :can-close="settingsConfigured"
        @close="showSettings = false"
        @settings-loaded="handleSettingsLoaded"
      />

      <div
        class="p-4 space-y-3 border-b border-gray-200 dark:border-gray-700"
        v-if="!showSettings"
      >
        <button
          @click="showCustomerSearch = true"
          class="w-full px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          <div
            v-if="pos.selectedPosInvoice?.invoice.customer"
            class="flex items-center justify-between"
          >
            <div>
              <div class="text-sm font-medium text-blue-600 dark:text-blue-400">
                Customer
              </div>
              <div
                class="text-base font-semibold text-gray-900 dark:text-white"
              >
                {{ pos.selectedPosInvoice?.invoice.customer.name }}
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-400">
                {{ pos.selectedPosInvoice?.invoice.customer.mobile }}
              </div>
            </div>
            <button
              @click.stop="clearCustomer"
              class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div v-else class="flex items-center justify-center space-x-2">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span class="text-blue-600 dark:text-blue-400 font-medium"
              >Select Customer</span
            >
          </div>
        </button>

        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search products..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <svg
            class="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
    </div>

    <div class="p-4" v-if="!showSettings">
      <div v-if="filteredProducts.length === 0" class="text-center py-12">
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <p class="mt-4 text-gray-500 dark:text-gray-400">No products found</p>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <template v-for="product in filteredProducts" :key="product.id">
          <button
            @click="handleProductClick(product)"
            class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 text-left hover:shadow-md transition-shadow"
          >
            <AsyncImage :src="product.photo" :allow-preview="false" />
            <div class="space-y-1">
              <h3
                class="font-medium text-gray-900 dark:text-white text-sm line-clamp-2"
              >
                {{ product.name }}
              </h3>
              <p class="text-lg font-bold text-blue-600 dark:text-blue-400">
                {{ formatNumber(product.selling_price) }}
              </p>
              <p
                v-if="product.on_hand !== undefined"
                class="text-xs text-gray-500 dark:text-gray-400"
              >
                Stock: {{ product.on_hand }}
              </p>
            </div>
          </button>

          <template
            v-if="product.packing_prices && product.packing_prices.length > 0"
          >
            <button
              v-for="packing in product.packing_prices.filter(
                (p) => p.price_lists_id == posSettings.price_list_id
              )"
              :key="packing.code"
              @click="handlePackingClick(packing, product)"
              class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 text-left hover:shadow-md transition-shadow"
            >
              <AsyncImage :src="product.photo" :allow-preview="false" />
              <div class="space-y-1">
                <h3
                  class="font-medium text-gray-900 dark:text-white text-sm line-clamp-2"
                >
                  {{ product.name }}
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400"
                  >{{ packing.unit_of_measure }}
                  <span v-if="(packing.inventory_qty ?? 0) > 1"
                    >x {{ packing.inventory_qty }} {{ product.unit_of_measure }}
                  </span></span
                >
                <p class="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {{ formatNumber(packing.selling_price) }}
                </p>
                <p
                  v-if="product.on_hand !== undefined"
                  class="text-xs text-gray-500 dark:text-gray-400"
                >
                  Stock:
                  {{
                    formatNumber(
                      (product.on_hand ?? 0) / (packing.inventory_qty ?? 1)
                    )
                  }}
                </p>
              </div>
            </button>
          </template>
        </template>
      </div>
    </div>

    <button
      v-if="(pos.selectedPosInvoice?.invoice?.items?.length ?? 0) > 0"
      @click="showCartDrawer = true"
      class="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-blue-600 text-white p-4 rounded-l-full shadow-lg hover:bg-blue-700 transition-all hover:pr-5"
    >
      <div class="flex items-center space-x-2">
        <div class="relative">
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <span
            class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
          >
            {{ pos.selectedPosInvoice?.invoice?.items?.length ?? 0 }}
          </span>
        </div>
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </div>
    </button>

    <Transition name="drawer">
      <div v-if="showCartDrawer" class="fixed inset-0 z-50 overflow-hidden">
        <div
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="showCartDrawer = false"
        ></div>

        <div
          class="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-xl flex flex-col"
        >
          <div
            class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700"
          >
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
              Cart ({{
                pos.selectedPosInvoice?.invoice?.items?.length ?? 0
              }}
              items)
            </h2>
            <button
              @click="showCartDrawer = false"
              class="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-4 space-y-3">
            <div
              v-for="item in pos.selectedPosInvoice?.invoice?.items ?? []"
              :key="item.id"
              class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
            >
              <div class="flex items-start space-x-3">
                <div
                  class="w-16 h-16 flex-shrink-0 flex items-center justify-center"
                >
                  <AsyncImage :src="item.photo" />
                </div>
                <div class="flex-1 min-w-0">
                  <h3
                    class="text-sm font-semibold text-gray-900 dark:text-white mb-1"
                  >
                    {{ item.name }}
                  </h3>
                  <p class="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {{ formatNumber(item.price * item.quantity) }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatNumber(item.price) }} each
                  </p>
                  <div class="flex items-center space-x-2 mt-2">
                    <button
                      @click="
                        item.quantity - 1 < 1
                          ? pos.removeItem(item)
                          : pos.updateQuantity(item, item.quantity - 1)
                      "
                      class="w-8 h-8 flex items-center justify-center bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M20 12H4"
                        />
                      </svg>
                    </button>
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white min-w-[2rem] text-center"
                    >
                      {{ item.quantity }}
                    </span>
                    <button
                      @click="pos.updateQuantity(item, item.quantity + 1)"
                      class="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary -->
          <div
            class="border-t border-gray-200 dark:border-gray-700 pl-4 pr-4 pb-4 space-y-2 bg-gray-50 dark:bg-gray-900 relative"
          >
            <div
              class="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10"
            >
              <button
                @click="isSummaryExpanded = !isSummaryExpanded"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 shadow-md"
                :class="{ 'rotate-180': isSummaryExpanded }"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            <div class="space-y-2 pt-2">
              <Transition name="fade">
                <div v-if="isSummaryExpanded" class="space-y-2">
                  <div
                    class="flex items-center justify-between text-sm pb-2 border-b-2 border-gray-300 dark:border-gray-600"
                  >
                    <span class="text-gray-600 dark:text-gray-400"
                      >Items Total</span
                    >
                    <span class="font-medium text-gray-900 dark:text-white">{{
                      formatCurrency(pos.sub_total ?? 0)
                    }}</span>
                  </div>

                    <div
                    class="flex items-center justify-between text-sm pb-2 border-b-2 border-gray-300 dark:border-gray-600"
                    >
                    <span class="text-gray-600 dark:text-gray-400"
                      >Discount</span
                    >
                    <div class="flex items-center space-x-2">
                      <NumberInput
                      v-model="pos.discount"
                      class="w-16 text-right"
                      size="sm"
                      max-width="100px"
                      :show-controls="false"
                      @input="pos.applyDiscount(pos.discount)"
                      />
                      <span class="font-medium text-red-600 dark:text-red-400">
                      -{{ formatCurrency(pos.discount_amount ?? 0) }}
                      </span>
                    </div>
                    </div>

                  <div
                    class="flex items-center justify-between text-sm pb-2 border-b-2 border-gray-300 dark:border-gray-600"
                  >
                    <span class="text-gray-600 dark:text-gray-400"
                      >Service Charge</span
                    >
                    <span class="font-medium text-gray-900 dark:text-white">{{
                      formatCurrency(pos.service_charge ?? 0)
                    }}</span>
                  </div>

                  <div
                    class="flex items-center justify-between text-sm pb-2 border-b-2 border-gray-300 dark:border-gray-600"
                  >
                    <span class="text-gray-600 dark:text-gray-400"
                      >Value before Tax</span
                    >
                    <span class="font-medium text-gray-900 dark:text-white">{{
                      formatCurrency(pos.tax_amount ?? 0)
                    }}</span>
                  </div>
                </div>
              </Transition>

              <div
                class="flex items-center justify-between"
                :class="{ 'pt-0': !isSummaryExpanded }"
              >
                <span class="text-base font-bold text-gray-900 dark:text-white"
                  >Net Total</span
                >
                <span
                  class="text-2xl font-bold text-blue-600 dark:text-blue-400"
                  >{{ formatCurrency(pos.net_total ?? 0) }}</span
                >
              </div>
            </div>

            <div class="flex gap-2 pt-2">
              <button
                @click="checkout"
                class="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>Checkout</span>
              </button>
              <button
                @click="clearCart"
                class="flex-1 px-4 py-2 border border-red-300 text-red-600 dark:border-red-700 dark:text-red-400 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <MobileCustomerSearch
      v-if="showCustomerSearch"
      @close="showCustomerSearch = false"
      :customer-groups="customerGroups"
      :customer-types="customerTypes"
      :countrys="countrys"
      :customers="customers"
      @load-customers="loadCustomers"
    />

    <MobileCheckout
      v-if="showCheckout"
      :cashier-duty="null"
      :delivery_staffs="deliveryStaff"
      :pos-settings="posSettings"
      :sales-reps="salesReps"
      :payment_types="paymentTypes"
      :pos-station="undefined"
      @close="showCheckout = false"
      @complete="completeCheckout"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import Toast from "../../../../../services/Toast";
import MobileCustomerSearch from "./MobileCustomerSearch.vue";
import MobileCheckout from "./MobileCheckout.vue";
import POSSettingsPopover from "./POSSettingsPopover.vue";
import { api } from "../../../../../api/Api";
import { Item, ItemPackingPrice } from "../../../../../types/models";
import {
  formatCurrency,
  formatNumber,
} from "../../../../../utils/CommonFunctions";
import AsyncImage from "../../../../../components/ui/form/AsyncImage.vue";
import { posStore } from "../../../../../stores/posStore";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";

const pos = posStore();

const searchQuery = ref("");
const products = ref<Item[]>([]);
const showCustomerSearch = ref(false);
const showCheckout = ref(false);
const showCartDrawer = ref(false);
const showSettings = ref(false);
const hasLoadedSettings = ref(false);
const settingsConfigured = ref(false);
const posSettings = ref({
  warehouse_id: "",
  price_list_id: "",
  sales_channel_id: "",
});

const isSummaryExpanded = ref(false);

const filteredProducts = computed(() => {
  if (!searchQuery.value) return products.value;
  const query = searchQuery.value.toLowerCase();
  return products.value.filter(
    (p) =>
      query
        .split(" ")
        .every((word) => (p.name ?? "").toLowerCase().includes(word)) ||
      p.code?.toLowerCase() == query.toLowerCase()
  );
});

const loadProducts = async (search: string) => {
  const params = {
    search_query: search || "",
    price_lists_id: posSettings.value.price_list_id,
    warehouses_id: posSettings.value.warehouse_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    products.value = response.data;
  } else {
    Toast.show("Item not found", "warning");
  }
};

const handleProductClick = (product: Item) => {
  pos.addProductToInvoice(product);
  console.log("pos items", pos.selectedPosInvoice?.invoice?.items);
  Toast.show(`Added ${product.name} to cart`, "info", 500);
};

const handlePackingClick = (packing: ItemPackingPrice, product: Item) => {
  let prod: Item = {
    item_name:
      product.name +
      " " +
      packing.unit_of_measure +
      (packing.inventory_qty > 1 ? "x" + packing.inventory_qty : ""),
    id: product.id,
    photo: product.photo, //TODO: seperate photo for packing prices
    code: packing.code,
    name:
      product.name +
      " " +
      packing.unit_of_measure +
      (packing.inventory_qty > 1 ? "x" + packing.inventory_qty : ""),
    item:
      product.name +
      " " +
      packing.unit_of_measure +
      (packing.inventory_qty > 1 ? "x" + packing.inventory_qty : ""),
    item_type: product.item_type,
    sales_name:
      product.name +
      " " +
      packing.unit_of_measure +
      (packing.inventory_qty > 1 ? "x" + packing.inventory_qty : ""),
    item_types_id: product.item_types_id,
    tracked_by: product.tracked_by,
    sales_uom: packing.unit_of_measure,
    sales_uom_qty: packing.inventory_qty,
    is_package: product.is_package,
    use_package_price: product.use_package_price,
    flexi_price: product.flexi_price,
    ignore_discount: product.ignore_discount,
    is_expiry: product.is_expiry,
    tax_details_id: product.tax_details_id,
    inventory_uom_id: product.inventory_uom_id,
    inventory_uom_qty: product.inventory_uom_qty,
    sales_uom_id: packing.unit_of_measures_id,
    has_variants: product.has_variants,
    variants: product.variants,
    tax: product.tax,
    tax_rate: product.tax_rate,
    uid: product.uid,
    warehouses_id: posSettings.value.warehouse_id,
    price_lists_id: posSettings.value.price_list_id,
    items_id: product.id,
    item_variants_id: product.item_variants_id,
    unit_of_measures_id: packing.unit_of_measures_id,
    unit_of_measure: packing.unit_of_measure,
    on_hand: product.on_hand,
    quantity: 1,
    uom_qty: packing.inventory_qty,
    inventory_qty: 1 * packing.inventory_qty,
    cost: product.cost,
    original_price: packing.selling_price,
    selling_price: packing.selling_price,
    price: packing.selling_price,
    sub_total: packing.selling_price,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: product.selling_price,
    tax_amount: 0,
    net_total: 0,
    remarks: "",
    batches: [],
    serials: [],
    quantity_prices: [],
    packing_prices: [],
    barcodes: [],
    shelfs: [],
    variant_list: [],
    variant_name: product.variant_name,
    showPackingPrices: product.showPackingPrices,
    isFocused: false,
    isHovered: false,
    isExpanded: false,
    default_sales_uom_id: packing.unit_of_measures_id,
    default_sales_uom_qty: packing.inventory_qty,
  };
  pos.addProductToInvoice(prod);
  Toast.show(`Added ${prod.name} to cart`, "info", 500);
};

const clearCustomer = () => {
  pos.clearCustomer();
};



const clearCart = () => {
  pos.voidInvoice();
  showCartDrawer.value = false;
  Toast.show("Cart cleared", "info");
};

const checkout = () => {
  if (!settingsConfigured.value) {
    Toast.show("Please configure POS settings first", "error");
    showCartDrawer.value = false;
    showSettings.value = true;
    return;
  }
  if (!pos.selectedPosInvoice?.invoice.customer) {
    Toast.show("Please select a customer first", "error");
    return;
  }
  showCartDrawer.value = false;
  showCheckout.value = true;
};

const completeCheckout = async () => {
  try {
    Toast.show("Sale completed successfully!", "info");
    pos.voidInvoice();
    showCheckout.value = false;
  } catch (error) {
    console.error("Error completing checkout:", error);
    Toast.show("Failed to complete checkout", "error");
  }
};

const toggleSettingsPopover = () => {
  showSettings.value = !showSettings.value;
};

const handleSettingsLoaded = (settings: any) => {
  hasLoadedSettings.value = true;
  posSettings.value = settings;
  settingsConfigured.value = !!(
    settings.warehouse_id &&
    settings.price_list_id &&
    settings.sales_channel_id
  );
  console.log("settingsConfigured:", settingsConfigured.value);
  if (settingsConfigured.value) {
    console.log("Settings are configured, closing popover");
    showSettings.value = false;
  } else {
    console.log("Settings are NOT configured, keeping popover open");
  }
};

const checkAndShowSettings = async () => {
  posSettings.value = {
    warehouse_id: localStorage.getItem("fp_pos_warehouse_id") || "",
    price_list_id: localStorage.getItem("fp_pos_price_list_id") || "",
    sales_channel_id: localStorage.getItem("fp_pos_sales_channel_id") || "",
  };

  if (
    posSettings.value.warehouse_id &&
    posSettings.value.price_list_id &&
    posSettings.value.sales_channel_id
  ) {
    settingsConfigured.value = true;
    loadProducts("");
  } else {
    toggleSettingsPopover();
    settingsConfigured.value = false;
  }

  console.log(
    "Loaded POS settings:",
    posSettings.value,
    "loaded",
    settingsConfigured.value,
    "showSettings",
    showSettings.value
  );
};

const customerGroups = ref<any[]>([]);
const customerTypes = ref<any[]>([]);
const countrys = ref<any[]>([]);
const customers = ref<any[]>([]);
const paymentTypes = ref<any[]>([]);
const itemCategories = ref<any[]>([]);
const deliveryStaff = ref<any[]>([]);
const salesReps = ref<any[]>([]);

const loadDefaults = async () => {
  const response = await api.post("revenue/sales/pos/mobile-pos", {});
  if (response.success == true) {
    customerGroups.value = response.data.customerGroups || [];
    customerTypes.value = response.data.customerTypes || [];
    countrys.value = response.data.countrys || [];
    paymentTypes.value = response.data.payment_types || [];
    itemCategories.value = response.data.itemCategories || [];
    deliveryStaff.value = response.data.deliveryStaff || [];
    salesReps.value = response.data.salesReps || [];
  } else {
    Toast.show("Could not load defaults", "warning");
  }
};

const loadCustomers = async () => {
  const response = await api.post(
    "revenue/master-data/customers/list",
    {
      details: true,
    },
    {
      skipLoading: true,
    }
  );
  if (response.success == true) {
    customers.value = response.data || [];
  } else {
    Toast.show("Could not Customers", "warning");
  }
};

onMounted(() => {
  checkAndShowSettings();
  loadDefaults();
  pos.addNewPosInvoice();
  loadCustomers();
});
</script>

<style scoped>
.drawer-enter-active,
.drawer-leave-active {
  transition: all 0.3s ease;
}

.drawer-enter-active .absolute.right-0,
.drawer-leave-active .absolute.right-0 {
  transition: transform 0.3s ease;
}

.drawer-enter-from .absolute.right-0,
.drawer-leave-to .absolute.right-0 {
  transform: translateX(100%);
}

.drawer-enter-from .absolute.inset-0,
.drawer-leave-to .absolute.inset-0 {
  opacity: 0;
}
</style>
